import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

/**
 * Custom hook to fetch and manage tenant configuration with local caching
 */
export const useTenantConfig = () => {
  const { user, getCurrentTenant } = useAuth();
  const [tenantConfig, setTenantConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Cache key for localStorage
  const getCacheKey = (tenantCode) => `tenant_config_${tenantCode}`;
  const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  // Get cached config with TTL check
  const getCachedConfig = (tenantCode) => {
    try {
      const cachedData = localStorage.getItem(getCacheKey(tenantCode));
      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        if (Date.now() - timestamp < CACHE_DURATION) return data;
        localStorage.removeItem(getCacheKey(tenantCode));
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.warn('Tenant config cache read error:', error);
    }
    return null;
  };

  // Save config to cache
  const setCachedConfig = (tenantCode, config) => {
    try {
      localStorage.setItem(getCacheKey(tenantCode), JSON.stringify({ data: config, timestamp: Date.now() }));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.warn('Tenant config cache save error:', error);
    }
  };

  // Define fetchTenantConfig as useCallback so it can be used in refetch
  const fetchTenantConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const currentTenant = getCurrentTenant();
      let tenantInfo = currentTenant;
      if (!tenantInfo && user) {
        if (user.tenants && user.tenants.length > 0) {
          tenantInfo = user.tenants[0];
        } else if (user.userType === 'admin') {
          tenantInfo = { code: 'admin-default' };
        }
      }
      if (!tenantInfo) {
        setTenantConfig({
          company_name: 'No tenant',
          company_logo: null,
          website: null
        });
        return;
      }

      const tenantCode = tenantInfo.code || tenantInfo.tenant_code || 'admin-default';

      // Check cache first
      const cachedConfig = getCachedConfig(tenantCode);
      if (cachedConfig) {
        setTenantConfig(cachedConfig);
        return;
      }

      // Step 3: If user has tenant data from auth, use it as baseline
      let baseConfig = {
        company_name: tenantInfo.name || tenantInfo.tenant_name || 'CRM System',
        company_logo: null,
        website: null,
        tenant_name: tenantInfo.name || tenantInfo.tenant_name
      };

      try {
        const response = await api.get('/tenant-info/current');
        if (response.data?.tenantInfo) {
          baseConfig = {
            ...baseConfig,
            ...response.data.tenantInfo
          };
        }
      } catch (apiError) {
        // Use baseConfig from auth context
      }

      // Step 5: Cache the result and set state
      setCachedConfig(tenantCode, baseConfig);
      setTenantConfig(baseConfig);

    } catch (err) {
      console.error('❌ Error fetching tenant config:', err);
      setError(err);
      // Use fallback on error
      setTenantConfig({
        company_name: 'CRM System',
        company_logo: null,
        website: null
      });
    } finally {
      setLoading(false);
    }
  }, [user, getCurrentTenant]);

  useEffect(() => {
    if (user) {
      fetchTenantConfig();
    } else {
      setLoading(false);
    }
  }, [user, fetchTenantConfig, refetchTrigger]);

  // Function to clear cache and refetch
  const refetch = useCallback(() => {
    const currentTenant = getCurrentTenant();
    let tenantInfo = currentTenant;
    
    // Use same logic as in fetchTenantConfig to find tenant
    if (!tenantInfo && user) {
      if (user.tenants && user.tenants.length > 0) {
        tenantInfo = user.tenants[0];
      } else if (user.userType === 'admin') {
        tenantInfo = { code: 'admin-default' };
      }
    }
    
    if (tenantInfo) {
      const tenantCode = tenantInfo.code || tenantInfo.tenant_code || 'admin-default';
      localStorage.removeItem(getCacheKey(tenantCode));
    }
    clearCache();
    
    // Reset state and trigger refetch
    setTenantConfig(null);
    setLoading(true);
    setError(null);
    
    // Trigger refetch by updating the trigger
    setRefetchTrigger(prev => prev + 1);
  }, [user, getCurrentTenant]);

  // Function to clear all tenant config caches
  const clearCache = () => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('tenant_config_')) localStorage.removeItem(key);
      });
    } catch (error) {
      console.warn('Error clearing tenant config cache:', error);
    }
  };

  // Get tenant code and name from the config
  const tenantCode = tenantConfig?.tenant_code || 'DEMO';
  const tenantName = tenantConfig?.company_name || tenantConfig?.tenant_name || 'Demo Tenant';

  return {
    tenantConfig,
    tenantCode,
    tenantName,
    loading,
    error,
    refetch,
    clearCache
  };
};